import { Injectable } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ModalNavigationService } from './modal-navigation.service';
import { SbModalState } from './modal-state.service';
import {
  AskOptions,
  ConfirmOptions,
  DatePickOptions,
  DatePickResult,
  ErrorOptions,
  FreetextFilteredOptions,
  FreetextOptions,
  FreetextResult,
  ModalSettings,
  MultiFreetextFilteredOptions,
  NavigationOptions,
  Options,
  OverrideOptions,
  OverrideResult,
  SearchSelectOptions,
  SearchSelectResult,
  SelectDetailsOptions,
  SelectDetailsResult,
  SelectOptions,
  SelectResult,
} from './modal.model';
import { BookingDTO } from '../../api/api';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  constructor(
    private modalService: NgbModal,
    private state: SbModalState,
    private modalNavigation: ModalNavigationService
  ) {}

  showNavigationModal<T>(options: NavigationOptions<T>) {
    const templateId = 'sbNavigationModal';
    this.modalNavigation.navigate(options.entryComponent.value, options.entryComponent.data);
    return new Promise((resolve, rej) => {
      this.state.navigationSharedData = {};
      this.state.options.set(templateId, options);
      let modalOptions = options.modalOptions;
      if (!modalOptions) {
        modalOptions = {};
      }
      modalOptions.beforeDismiss = () =>
        new Promise<boolean>((subresolve, subreject) => {
          if (this.state?.navigationStack?.peek()?.componentInstance?.blockBackdrop) {
            subreject();
          } else {
            subresolve(true);
          }
        });
      this.state.modalOptions.set(templateId, modalOptions);
      const modalRef: NgbModalRef = this.modalService.open(this.state.templates.get(templateId), this.state.modalOptions.get(templateId));
      this.state.modals.set(templateId, modalRef);
      this.state.modals
        .get(templateId)
        .result.then(res => {
          resolve(res);
        })
        .catch(e => {
          rej(e);
        });
    });
  }

  freetext<T>(options: FreetextOptions<T>): Promise<FreetextResult<T>> {
    const templateId = 'freetextModal';
    return new Promise((resolve, rej) => {
      this.state.options.set(templateId, options);
      this.state.modalOptions.set(templateId, options.modalOptions);
      this.state.modals.set(
        templateId,
        this.modalService.open(this.state.templates.get(templateId), this.state.modalOptions.get(templateId))
      );
      this.state.modals
        .get(templateId)
        .result.then(res => {
          resolve(res);
        })
        .catch(e => {
          rej(e);
        });
    });
  }

  freetextFiltered<T>(options: FreetextFilteredOptions<T>): Promise<FreetextResult<T>> {
    const templateId = 'freetextFilteredModal';
    return new Promise((resolve, rej) => {
      this.state.options.set(templateId, options);
      this.state.modalOptions.set(templateId, options.modalOptions);
      this.state.modals.set(
        templateId,
        this.modalService.open(this.state.templates.get(templateId), this.state.modalOptions.get(templateId))
      );
      this.state.modals
        .get(templateId)
        .result.then(res => {
          resolve(res);
        })
        .catch(e => {
          rej(e);
        });
    });
  }

  multiFreetextFiltered<T>(options: MultiFreetextFilteredOptions<T>): Promise<FreetextResult<T>> {
    const templateId = 'multiFreetextFilteredModal';
    return new Promise((resolve, rej) => {
      this.state.options.set(templateId, options);
      this.state.modalOptions.set(templateId, options.modalOptions);
      this.state.modals.set(
        templateId,
        this.modalService.open(this.state.templates.get(templateId), this.state.modalOptions.get(templateId))
      );
      this.state.modals
        .get(templateId)
        .result.then(res => {
          resolve(res);
        })
        .catch(e => {
          rej(e);
        });
    });
  }

  multiSelect<T>(options: MultiFreetextFilteredOptions<T>): Promise<FreetextResult<T[]>> {
    const templateId = 'multiSelectModal';
    return new Promise((resolve, rej) => {
      this.state.options.set(templateId, options);
      this.state.modalOptions.set(templateId, options.modalOptions);
      this.state.modals.set(
        templateId,
        this.modalService.open(this.state.templates.get(templateId), this.state.modalOptions.get(templateId))
      );
      this.state.modals
        .get(templateId)
        .result.then(res => {
          resolve(res);
        })
        .catch(e => {
          rej(e);
        });
    });
  }

  searchSelect<T>(options: SearchSelectOptions<T>): Promise<SearchSelectResult<T>> {
    const templateId = 'searchSelectModal';
    return new Promise((resolve, rej) => {
      this.state.options.set(templateId, options);
      this.state.modalOptions.set(templateId, options.modalOptions);
      this.state.modals.set(
        templateId,
        this.modalService.open(this.state.templates.get(templateId), this.state.modalOptions.get(templateId))
      );
      this.state.modals
        .get(templateId)
        .result.then(res => {
          resolve(res);
        })
        .catch(e => {
          rej(e);
        });
    });
  }

  confirm<T>(options: ConfirmOptions<T>): Promise<boolean> {
    const templateId = 'askmodal';
    return new Promise(resolve => {
      this.state.options.set(templateId, options);
      this.state.modalOptions.set(templateId, options.modalOptions);
      this.state.modals.set(
        templateId,
        this.modalService.open(this.state.templates.get(templateId), this.state.modalOptions.get(templateId))
      );
      this.state.modals
        .get(templateId)
        .result.then(res => {
          if (res === 'YES') {
            resolve(true);
          } else {
            resolve(false);
          }
        })
        .catch(() => {
          resolve(false);
        });
    });
  }

  select<T>(options: SelectOptions<T>): Promise<SelectResult<T>> {
    const templateId = 'selectmodal';
    return new Promise(resolve => {
      this.state.options.set(templateId, options);
      this.state.modalOptions.set(templateId, options.modalOptions);
      this.state.modals.set(
        templateId,
        this.modalService.open(this.state.templates.get(templateId), this.state.modalOptions.get(templateId))
      );
      this.state.modals
        .get(templateId)
        .result.then(res => {
          resolve(res);
        })
        .catch(() => {
          resolve({
            value: null,
            cancel: true,
          });
        });
    });
  }

  selectWithDetails<T>(options: SelectDetailsOptions<T>): Promise<SelectDetailsResult<T>> {
    const templateId = 'selectdetailsmodal';
    return new Promise(resolve => {
      this.state.options.set(templateId, options);
      this.state.modalOptions.set(templateId, options.modalOptions);
      this.state.modals.set(
        templateId,
        this.modalService.open(this.state.templates.get(templateId), this.state.modalOptions.get(templateId))
      );
      this.state.modals
        .get(templateId)
        .result.then(res => {
          resolve(res);
        })
        .catch(() => {
          resolve({
            value: null,
            cancel: true,
          });
        });
    });
  }

  datepicker<T>(
    date: boolean,
    time: boolean,
    initial: DatePickResult,
    title?: string,
    modalOptions?: NgbModalOptions
  ): Promise<DatePickResult> {
    const templateId: string = 'datePickerModal';
    const options: DatePickOptions<T> = {
      date,
      time,
      initial,
      modalOptions,
      title,
    };
    return new Promise(resolve => {
      this.state.options.set(templateId, options);
      this.state.modalOptions.set(templateId, options.modalOptions);
      this.state.modals.set(
        templateId,
        this.modalService.open(this.state.templates.get(templateId), this.state.modalOptions.get(templateId))
      );
      this.state.modals
        .get(templateId)
        .result.then(res => {
          resolve(res);
        })
        .catch(() => {
          resolve({
            cancel: true,
          });
        });
    });
  }

  mediumGroupOverride<T>(initial: OverrideResult, bookings: BookingDTO[], modalOptions?: NgbModalOptions): Promise<OverrideResult> {
    const templateId = 'mediumGroupOverrideModal';
    const options: OverrideOptions<T> = {
      initial,
      modalOptions,
      title: 'Override Motivegroup',
      bookings,
    };
    return new Promise((resolve, rej) => {
      this.state.options.set(templateId, options);
      this.state.modalOptions.set(templateId, options.modalOptions);
      this.state.modals.set(
        templateId,
        this.modalService.open(this.state.templates.get(templateId), this.state.modalOptions.get(templateId))
      );
      this.state.modals
        .get(templateId)
        .result.then(res => {
          resolve(res);
        })
        .catch(e => {
          rej(e);
        });
    });
  }

  alert(title?: string, text?: string, ok?: string, modalOptions?: NgbModalOptions): Promise<string> {
    return this.ask({
      title: title ? title : 'Alert',
      message: text ? text : '',
      choices: [
        {
          title: ok ? ok : 'OK',
          value: 'OK',
        },
      ],
      onClose: {
        title: 'none',
        value: 'none',
      },
      modalOptions,
    });
  }

  error<T>(options: ErrorOptions<T>): Promise<boolean> {
    const templateId = 'errormodal';
    return new Promise(resolve => {
      this.state.options.set(templateId, options);
      this.state.modalOptions.set(templateId, options.modalOptions);
      this.state.modals.set(
        templateId,
        this.modalService.open(this.state.templates.get(templateId), this.state.modalOptions.get(templateId))
      );
      this.state.modals
        .get(templateId)
        .result.then(() => {
          resolve(true);
        })
        .catch(() => {
          resolve(true);
        });
    });
  }

  errorUncaught<T>(options: ErrorOptions<T>): Promise<boolean> {
    const templateId = 'uncaughtmodal';
    return new Promise(resolve => {
      this.state.options.set(templateId, options);
      this.state.modalOptions.set(templateId, options.modalOptions);
      this.state.modals.set(
        templateId,
        this.modalService.open(this.state.templates.get(templateId), this.state.modalOptions.get(templateId))
      );
      this.state.modals
        .get(templateId)
        .result.then(() => {
          resolve(true);
        })
        .catch(() => {
          resolve(true);
        });
    });
  }

  errorForbidden<T>(options: ErrorOptions<T>): Promise<boolean> {
    const templateId = 'forbiddenmodal';
    return new Promise(resolve => {
      this.state.options.set(templateId, options);
      this.state.modalOptions.set(templateId, options.modalOptions);
      this.state.modals.set(
        templateId,
        this.modalService.open(this.state.templates.get(templateId), this.state.modalOptions.get(templateId))
      );
      this.state.modals
        .get(templateId)
        .result.then(() => {
          resolve(true);
        })
        .catch(() => {
          resolve(true);
        });
    });
  }

  ask<T>(options: AskOptions<T>): Promise<string> {
    const templateId = 'alertmodal';
    return new Promise(resolve => {
      this.state.options.set(templateId, options);
      this.state.modalOptions.set(templateId, options.modalOptions);
      this.state.modals.set(
        templateId,
        this.modalService.open(this.state.templates.get(templateId), this.state.modalOptions.get(templateId))
      );
      this.state.modals
        .get(templateId)
        .result.then(res => {
          if (!res) {
            resolve(options.onClose.value);
          } else {
            resolve(res);
          }
        })
        .catch(e => {
          if (!e) {
            resolve(options.onClose.value);
          } else {
            resolve(e);
          }
        });
    });
  }

  modal<T>(options: Options<T>, settings: ModalSettings, payload?: any): Promise<any> {
    return new Promise((resolve, rej) => {
      const opts = options;
      opts.payload = payload;
      this.state.options.set(settings.templateId, opts);
      this.state.modalOptions.set(settings.templateId, opts.modalOptions);
      this.state.modals.set(
        settings.templateId,
        this.modalService.open(this.state.templates.get(settings.templateId), this.state.modalOptions.get(settings.templateId))
      );
      this.state.modals
        .get(settings.templateId)
        .result.then(res => {
          resolve(res);
        })
        .catch(e => {
          rej(e);
        });
    });
  }

  getPayload(templateID: string): any {
    return this.state.options.get(templateID)?.payload;
  }

  hasOpenModals(): boolean {
    return this.modalService.hasOpenModals();
  }

  dismiss(template, value?) {
    this.state.modals.get(template)?.dismiss(value);
  }

  close(template, value?) {
    this.state.modals.get(template)?.close(value);
  }

  dismissAll() {
    this.modalService.dismissAll();
  }
}
